import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  MFA_IDENTITY_TYPES,
  type MFAIdentityType,
} from '@/entities/mfa-identity';
import type {
  DisableTwoFactorIdentityRequestDto,
  DisableTwoFactorIdentityResponseDto,
} from '../api/types';
import { useTwoFactorDisable } from '../api/queries/useTwoFactorDisable';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '@/entities/session';
import { isVerificationCodeInvalidError } from '@/shared/utils';

type TwoFactorIdentityDisableArgs = {
  identityType: MFAIdentityType;
  state: string;
  onSuccess: () => void;
};

const useTwoFactorIdentityDisableForm = ({
  identityType,
  state,
  onSuccess,
}: TwoFactorIdentityDisableArgs) => {
  const { session, refreshSession } = useSessionStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const required = t('authPages.validation.requiredField');
  const otpIncorrect = t('authPages.validation.otpIncorrect');

  const otpCodeSchema = z
    .string()
    .regex(/^\d+$/, { message: otpIncorrect })
    .nonempty({ message: required });

  const schema = z.object({
    twoFA: z.object({
      state: z.string().optional(),
      identityType: z.enum([...Object.values(MFA_IDENTITY_TYPES)]),
      code: otpCodeSchema,
    }),
  });

  const form = useForm<DisableTwoFactorIdentityRequestDto>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      twoFA: {
        state: state,
        identityType: identityType,
        code: '',
      },
    },
  });

  const {
    disableTwoFactorData,
    disableTwoFactor,
    isDisableTwoFactorLoading,
    isDisableTwoFactorError,
    disableTwoFactorError,
    resetDisableTwoFactor,
  } = useTwoFactorDisable({
    onSuccess: async (data: DisableTwoFactorIdentityResponseDto) => {
      // If the code is invalid, reset the otp code field
      if (isVerificationCodeInvalidError(data)) {
        form.setValue('twoFA.code', '');
      }
      // Empty response means actual success
      if (data && typeof data !== 'object') {
        await queryClient.invalidateQueries({
          queryKey: ['user-profile-two-factor-settings', session?.userId],
        });
        await refreshSession();
        onSuccess();
      }
    },
  });

  const submitForm = async () => {
    await disableTwoFactor(form.getValues());
  };

  return {
    disableTwoFactorData,
    disableForm: form,
    disableTwoFactorSubmit: submitForm,
    isDisableTwoFactorLoading,
    isDisableTwoFactorError,
    disableTwoFactorError,
    resetDisableTwoFactor,
  };
};

export { useTwoFactorIdentityDisableForm };
